import { useEffect, useRef, useState } from "react";
import DeleteIcon from "components/SVGs/Delete";
import { IChangedTarget, RenderInput } from "../../common/Elements";
import { useTranslation } from "react-i18next";
import { IStudentRecord } from "./AddingIndividually";
import { validateProperty } from "./utils/validate";
import { IValidationErrors } from "features/common/Interfaces";
import { Typography } from "@mui/material";

const customStyles = {
  form: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    flexDirection: "row",
    width: "100%",
    "&:hover": {
      background: "rgba(181,181,181,0.2)",
      borderRadius: "5px",
    },
  },
};
export interface IStudentRecordInput {
  currentTarget: { name: "name" | "studentId" | "email"; value: string };
}
type Props = {
  index: number;
  data: IStudentRecord;
  validationErrors: IValidationErrors;
  getRecord(data: IStudentRecord, index: number): void;
  removeRecord(index: number): void;
  noLabel?: boolean;
};

const StudentRecord = ({
  index,
  data,
  validationErrors,
  getRecord,
  removeRecord,
  noLabel = false,
}: Props) => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState<IStudentRecord>(data);
  const [recordIndex] = useState(index);
  const [errors, setErrors] = useState<IValidationErrors>(validationErrors);
  const nameRef = useRef<HTMLInputElement>(null);
  const formVars = {
    studentName: {
      name: "name",
      label: !noLabel ? t("Student full name") : "",
      placeholder: t("Student full name"),
      value: formData.name,
      autoFocus: true,
      inputRef: nameRef,
    },
    studentId: {
      name: "studentId",
      label: !noLabel ? t("Student Id") : "",
      placeholder: t("StudentId"),
      value: formData.studentId,
      autoFocus: false,
    },
    email: {
      name: "email",
      label: !noLabel ? t("Email optional") : "",
      placeholder: t("Email optional"),
      value: formData.email,
      autoFocus: false,
    },
  };

  useEffect(() => {
    if (nameRef.current) {
      nameRef.current.focus();
    }
  }, []);
  useEffect(() => {
    setErrors(validationErrors);
  }, [JSON.stringify(validationErrors)]);

  const changeHandler = (event: IChangedTarget) => {
    const data = { ...formData };
    const { currentTarget: input } = event as IStudentRecordInput;
    const errorsObject: any = { ...errors };
    if (["email", "studentId"].includes(input.name) && input.value.length > 0)
      input.value = input.value.trim();
    const errorMesssage = validateProperty(input);
    if (errorMesssage) errorsObject[input.name] = errorMesssage;
    else delete errorsObject[input.name];
    setErrors(errorsObject);
    data[input.name] = input.value;
    setFormData(data);
  };
  const submitHandler = (event: React.FormEvent) => {
    event.preventDefault();
    getRecord(formData, index);
  };

  return (
    <Typography
      component="form"
      sx={{ ...customStyles.form, direction: i18n.dir() }}
      key={"record" + recordIndex}
      onSubmit={submitHandler}
    >
      {RenderInput({
        ...formVars.studentId,
        errors: t(errors.studentId),
        changeHandler,
        styles: { padding: "0 20px", width: "250px" },
        onBlur: submitHandler,
        tabIndex: index + 1,
      })}
      {RenderInput({
        ...formVars.studentName,
        errors: t(errors.name),
        changeHandler,
        styles: { padding: "0 20px", width: "300px" },
        onBlur: submitHandler,
        tabIndex: index + 2,
      })}
      {RenderInput({
        ...formVars.email,
        errors: t(errors.email),
        changeHandler,
        styles: { padding: "0 20px", width: "400px" },
        onBlur: submitHandler,
        tabIndex: index + 2,
      })}
      <DeleteIcon
        sx={{
          background: "rgb(105 123 140)",
          borderRadius: "50%",
          padding: "3px",
          margin: "5px 0",
          cursor: "pointer",
          visibility:
            formData.name.length || formData.studentId.length
              ? "visible"
              : "hidden",
          "&> path": { fill: "rgb(255 255 255)" },
        }}
        onClick={() => removeRecord(recordIndex)}
      />
      <button type="submit" style={{ visibility: "hidden" }} />
    </Typography>
  );
};

export default StudentRecord;
